<template>
    <div class="conditional-logic-expression conditional-logic-expression--StarRatingHybrid">
        <!-- Numeric Input for numeric operators -->
        <v-text-field
                v-if="isNumericOperator"
                v-model.number="expressionObj.statement"
                class="conditional-logic-expression__expression-input"
                placeholder="Enter value (1-5)"
                type="number"
                :min="1"
                :max="5"
                outlined
                hide-details
                dense
                @input.native="handleNumericInput"
        >
            <template #prepend-inner>
                <f-select
                        v-model="expressionObj.operator"
                        class="conditional-logic-expression__operator-picker"
                        :items="allOperators"
                        :item-text="item => item.text"
                        :item-value="item => item.value"
                        :menu-props="{
                            nudgeBottom: 54
                        }"
                        hide-details
                        solo flat
                        dense
                        @change="handleOperatorChange"
                />
            </template>
        </v-text-field>

        <!-- Options Input for options operators -->
        <f-autocomplete
                v-else
                v-model="conditionStatement"
                :items="optionsItems"
                :item-value="item => item.index"
                :filter="(item, search) => item.text.toLocaleLowerCase().includes(search.toLocaleLowerCase())"
                class="conditional-logic-expression__expression-input"
                ref="expressionInput"
                placeholder="Add option"
                :menu-props="{
                    maxWidth: optionsMenuWidth,
                    minWidth: optionsMenuWidth,
                    nudgeRight: optionsMenuNudgeRight,
                    nudgeBottom: 6,
                    closeOnClick: true
                }"
                :multiple="expressionObj.operator === 'matches_one_of'"
                outlined
                deletable-chips
                hide-details
                dense
        >
            <template #prepend-inner>
                <f-select
                        v-model="expressionObj.operator"
                        class="conditional-logic-expression__operator-picker"
                        ref="operatorPicker"
                        :items="allOperators"
                        :item-text="item => item.text"
                        :item-value="item => item.value"
                        :menu-props="{
                            nudgeBottom: 54
                        }"
                        hide-details
                        solo flat
                        dense
                        @click.stop
                        @change="handleOperatorChange"
                />
            </template>
            <template #item="{on, item, attrs}">
                <v-list-item v-on="on" v-bind="attrs">
                    <v-list-item-content>
                        <v-list-item-title>
                            {{ formatOptionLabel(item) }}
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </template>
            <template v-slot:selection="{ item, parent }">
                <v-chip
                        class="my-1"
                        :key="JSON.stringify(item)"
                        label
                        small
                >
                    <span class="text-truncate">{{ formatOptionLabel(item) }}</span>
                    <XIcon class="cursor-pointer ml-1" size="12" @click="expressionObj.operator === 'matches_one_of' ? parent.selectItem(item) : expressionObj.statement = []"/>
                </v-chip>
            </template>
        </f-autocomplete>
    </div>
</template>

<script>
    import {computed, onBeforeUnmount, onMounted, ref, watch} from '@vue/composition-api'
    import Vue from 'vue'

    export default {
        name: "StarRatingHybrid__ConditionalExpression",
        components: {
            XIcon: () => import('vue-feather-icons/icons/XIcon')
        },
        props: {
            expressionObj: Object
        },
        setup(props, {root, refs}) {
            // All available operators - both numeric and options
            const allOperators = [
                // Numeric operators
                {
                    text: 'Is equal to',
                    value: 'equals',
                    type: 'numeric'
                },
                {
                    text: 'Is not equal to',
                    value: 'not_equals',
                    type: 'numeric'
                },
                {
                    text: 'Is greater than',
                    value: 'greater_than',
                    type: 'numeric'
                },
                {
                    text: 'Is lower than',
                    value: 'lower_than',
                    type: 'numeric'
                },
                {
                    text: 'Greater or equal to',
                    value: 'greater_or_equal',
                    type: 'numeric'
                },
                {
                    text: 'Lower or equal to',
                    value: 'lower_or_equal',
                    type: 'numeric'
                },
                // Options operators
                {
                    text: 'Matches',
                    value: 'matches',
                    type: 'options'
                },
                {
                    text: 'Matches one of these',
                    value: 'matches_one_of',
                    type: 'options'
                },
                {
                    text: 'Doesn\'t Match',
                    value: 'doesnt_match',
                    type: 'options'
                }
            ]

            // Default operator
            if (!props.expressionObj.operator) {
                props.expressionObj.operator = allOperators[0].value
            }

            // Detect if current operator is numeric
            const isNumericOperator = computed(() => {
                const operator = allOperators.find(op => op.value === props.expressionObj.operator)
                return operator?.type === 'numeric'
            })

            // Get options for the options-based operators
            const optionsItems = computed(() => {
                if (isNumericOperator.value) return []
                return root.$store.getters['edit/conditional/getConditionalFieldConst']
                    .CONDITIONAL_LOGIC.PARAMS.VALUE_FORMATTER(root.$store.getters['edit/conditional/getConditionalField'])
            })

            // Handle operator change - reset statement when switching types
            const handleOperatorChange = (newOperator) => {
                const newOperatorObj = allOperators.find(op => op.value === newOperator)
                const currentOperatorObj = allOperators.find(op => op.value === props.expressionObj.operator)
                
                // If switching between numeric and options, reset statement
                if (newOperatorObj?.type !== currentOperatorObj?.type) {
                    if (newOperatorObj?.type === 'numeric') {
                        props.expressionObj.statement = 1 // Default to 1 for numeric
                    } else {
                        props.expressionObj.statement = [] // Default to empty array for options
                    }
                }
            }

            // Handle numeric input validation
            let previousValue = null
            const handleNumericInput = (e) => {
                Vue.nextTick(() => {
                    const value = parseInt(e.target.value)
                    if (value < 1 || value > 5) {
                        if ('inputType' in e) {
                            const stringValue = String(props.expressionObj.statement)
                            props.expressionObj.statement = +(stringValue.slice(0, stringValue.length - 1))
                        } else {
                            props.expressionObj.statement = previousValue
                        }
                    }
                    previousValue = props.expressionObj.statement
                })
            }

            // Options-related computed properties
            const conditionStatement = computed({
                get: () => {
                    if (isNumericOperator.value) return props.expressionObj.statement
                    return props.expressionObj.operator === 'matches_one_of' ? props.expressionObj.statement : props.expressionObj.statement[0]
                },
                set: (v) => {
                    if (isNumericOperator.value) {
                        props.expressionObj.statement = v
                    } else if (props.expressionObj.operator === 'matches_one_of') {
                        props.expressionObj.statement = v
                    } else {
                        props.expressionObj.statement.splice(0, 1, v)
                    }
                }
            })

            // Format option label for display
            const formatOptionLabel = (option) => {
                const letter = String.fromCharCode(65 + option.index) // A, B, C, etc.
                
                // Special handling for star rating emoji options
                if (option.isEmoji && option.emoji) {
                    return `${letter}: ${option.emoji}`
                }
                
                return `${letter}. ${option.text}`
            }

            // Menu width calculation for options dropdown
            const optionsMenuWidth = ref(0)
            const optionsMenuNudgeRight = ref(0)
            let unwatchMenuWidth

            const calculateMenuWidth = () => {
                if (refs.expressionInput && refs.operatorPicker) {
                    optionsMenuWidth.value = refs.expressionInput.$el.getBoundingClientRect().width - refs.operatorPicker.$el.getBoundingClientRect().width
                    optionsMenuNudgeRight.value = refs.operatorPicker.$el.getBoundingClientRect().width
                }
            }

            onMounted(() => {
                setTimeout(() => {
                    unwatchMenuWidth = watch(
                        () => props.expressionObj,
                        calculateMenuWidth,
                        { deep: true, immediate: true }
                    )
                }, 300)
            })

            onBeforeUnmount(() => {
                if (unwatchMenuWidth) {
                    unwatchMenuWidth()
                }
            })

            return {
                allOperators,
                isNumericOperator,
                optionsItems,
                conditionStatement,
                handleOperatorChange,
                handleNumericInput,
                formatOptionLabel,
                optionsMenuWidth,
                optionsMenuNudgeRight
            }
        }
    }
</script>

<style scoped>
.conditional-logic-expression--StarRatingHybrid {
    /* Inherit styles from parent conditional logic expression */
}
</style> 